import { useEffect, useRef, type CSSProperties } from "react";
import { X, Play, Square, Loader2 } from "lucide-react";
import { C, FONT, hexA } from "../theme";
import { Reservoir } from "./Reservoir";
import { Pill } from "./Pill";
import { MoistureTrend } from "./MoistureTrend";
import type { Zone, WeatherNow } from "../types";

/** One row of the root-zone checkbook. */
interface CheckRow {
  k: string;
  v: string;
  c: string;
}

/** Daily root-zone balance from real weather: ET₀ demand out, rain in (both from
 *  the live snapshot), plus a modeled irrigation top-up. Net is what moved the soil. */
function checkbook(z: Zone, weather: WeatherNow): CheckRow[] {
  const et0 = -weather.et0; // demand leaves the root zone
  const rain = weather.rainToday;
  // Drip beds and below-refill zones got a top-up cycle; healthy spray zones held.
  const applied = z.type === "drip" ? 3.4 : z.moisture <= z.mad ? 4.6 : 0;
  const net = et0 + rain + applied;
  const sign = (n: number) => (n >= 0 ? `+${n.toFixed(1)}` : `${n.toFixed(1)}`);
  return [
    { k: "ET₀ demand today", v: `${et0.toFixed(1)} mm`, c: C.beacon },
    { k: "Rainfall today", v: `${sign(rain)} mm`, c: C.ice },
    { k: "Irrigation applied", v: `${sign(applied)} mm`, c: C.sea },
    { k: "Net root-zone change", v: `${sign(net)} mm`, c: C.ink },
  ];
}

/** Right-anchored zone-detail slide-over: gauge, trend, root-zone checkbook, and actions. */
export function ZoneDetail({
  zone,
  weather,
  pending,
  onClose,
  onRun,
  onStop,
  onToggle,
}: {
  zone: Zone | null;
  weather: WeatherNow;
  pending: Record<string, boolean>;
  onClose: () => void;
  onRun: (id: string, minutes: number) => void;
  onStop: (id: string) => void;
  onToggle: (id: string, enabled: boolean) => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  // Close on Escape, and move focus to the close button when the panel opens.
  useEffect(() => {
    if (!zone) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [zone, onClose]);

  if (!zone) return null;
  const z = zone;
  const rows = checkbook(z, weather);
  const active = z.state === "watering" || z.state === "queued";
  const canRun = z.enabled && !active;
  const busyRun = !!pending[`run:${z.id}`];
  const busyStop = !!pending[`stop:${z.id}`];

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 40,
    display: "flex",
    justifyContent: "flex-end",
    background: hexA(C.ink, 0.34),
    backdropFilter: "blur(3px)",
    WebkitBackdropFilter: "blur(3px)",
  };

  const panel: CSSProperties = {
    height: "100%",
    width: "min(440px, 100%)",
    background: C.raise,
    borderLeft: `1px solid ${C.hairline}`,
    boxShadow: "var(--shadow-panel)",
    padding: 26,
    overflowY: "auto",
  };

  const closeBtn: CSSProperties = {
    display: "grid",
    placeItems: "center",
    width: 34,
    height: 34,
    borderRadius: 10,
    border: `1px solid ${C.hairline}`,
    background: C.paper,
    color: C.textMid,
    cursor: "pointer",
    flexShrink: 0,
  };

  const metaRow: CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    fontSize: 12.5,
  };

  const checkCard: CSSProperties = {
    background: C.paper,
    border: `1px solid ${C.line}`,
    borderRadius: 14,
    padding: "6px 16px",
  };

  const runBtn: CSSProperties = {
    flex: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: 13,
    borderRadius: 13,
    fontFamily: FONT.sans,
    fontSize: 14,
    fontWeight: 600,
    color: canRun ? C.onAccent : C.textLow,
    background: canRun ? C.seaDeep : C.track,
    border: `1px solid ${canRun ? C.seaDeep : C.hairline}`,
    cursor: !canRun ? "not-allowed" : busyRun ? "wait" : "pointer",
    opacity: busyRun ? 0.7 : 1,
  };

  const stopBtn: CSSProperties = {
    flex: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: 13,
    borderRadius: 13,
    fontFamily: FONT.sans,
    fontSize: 14,
    fontWeight: 600,
    color: C.onAccent,
    background: C.coral,
    border: `1px solid ${C.coral}`,
    cursor: busyStop ? "wait" : "pointer",
    opacity: busyStop ? 0.7 : 1,
  };

  const toggleBtn: CSSProperties = {
    padding: "13px 18px",
    borderRadius: 13,
    fontFamily: FONT.sans,
    fontSize: 14,
    fontWeight: 500,
    color: C.textMid,
    background: "transparent",
    border: `1px solid ${C.hairline}`,
    cursor: "pointer",
    flexShrink: 0,
  };

  return (
    <div className="overlay-in" style={overlay} onClick={onClose}>
      <div
        className="panel-in"
        style={panel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${z.name} zone detail`}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="serif" style={{ fontFamily: FONT.serif, fontSize: 23, fontWeight: 500, color: C.ink, lineHeight: 1.1 }}>
              {z.name}
            </div>
            <div style={{ fontSize: 12, color: C.mutedDeep, marginTop: 3 }}>
              {z.type === "drip" ? "Drip" : "Sprinkler"} · {z.area} m² · {z.plant}
            </div>
          </div>
          <button ref={closeRef} type="button" aria-label="Close" onClick={onClose} style={closeBtn}>
            <X size={16} />
          </button>
        </div>

        {/* Gauge + readout */}
        <div style={{ display: "flex", gap: 18, marginTop: 20, alignItems: "center" }}>
          <Reservoir z={z} w={94} h={198} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Pill z={z} big />
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 14 }}>
              <span
                className="serif tnum"
                style={{ fontFamily: FONT.serif, fontSize: 46, fontWeight: 400, color: C.ink, lineHeight: 0.85 }}
              >
                {Math.round(z.moisture)}
              </span>
              <span style={{ fontSize: 14, color: C.muted }}>% soil</span>
            </div>
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={metaRow}>
                <span style={{ color: C.textMid }}>Target band</span>
                <span className="mono" style={{ fontFamily: FONT.mono, color: C.sage }}>
                  {z.band[0]}–{z.band[1]}%
                </span>
              </div>
              <div style={metaRow}>
                <span style={{ color: C.textMid }}>Refill point</span>
                <span className="mono" style={{ fontFamily: FONT.mono, color: C.beacon }}>
                  {z.mad}%
                </span>
              </div>
              <div style={metaRow}>
                <span style={{ color: C.textMid }}>Last watered</span>
                <span style={{ color: C.ink }}>{z.lastRun}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 24h trend */}
        <div style={{ marginTop: 24 }}>
          <div className="eyebrow" style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            Soil moisture · last 24h
            <span style={{ fontWeight: 500, letterSpacing: 0, textTransform: "none", color: C.muted }}>· modeled</span>
          </div>
          <MoistureTrend z={z} />
        </div>

        {/* Root-zone checkbook */}
        <div style={{ marginTop: 20 }}>
          <div className="eyebrow" style={{ marginBottom: 9 }}>
            Root-zone checkbook
          </div>
          <div style={checkCard}>
            {rows.map((r, i) => (
              <div
                key={r.k}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "9px 0",
                  borderBottom: i < rows.length - 1 ? `1px solid ${C.lineSoft}` : "none",
                }}
              >
                <span style={{ color: C.textMid, fontSize: 12.5 }}>{r.k}</span>
                <span className="mono tnum" style={{ fontFamily: FONT.mono, color: r.c, fontSize: 13 }}>
                  {r.v}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 11, marginTop: 24 }}>
          {active ? (
            <button type="button" disabled={busyStop} onClick={() => onStop(z.id)} style={stopBtn}>
              {busyStop ? <Loader2 size={15} className="spin" /> : <Square size={13} fill={C.onAccent} />}
              {busyStop ? "Stopping…" : "Stop watering"}
            </button>
          ) : (
            <button
              type="button"
              disabled={!canRun || busyRun}
              onClick={() => {
                onRun(z.id, z.dur);
                onClose();
              }}
              style={runBtn}
            >
              {busyRun ? <Loader2 size={15} className="spin" /> : <Play size={15} fill={canRun ? C.onAccent : "none"} />}
              {busyRun ? "Starting…" : `Run ${z.dur} min`}
            </button>
          )}
          <button type="button" onClick={() => onToggle(z.id, !z.enabled)} style={toggleBtn}>
            {z.enabled ? "Disable" : "Enable"}
          </button>
        </div>
      </div>
    </div>
  );
}
